import React, { useEffect } from 'react';
import { StepProps } from '../../types/onboarding';
import { InfoIcon, HomeIcon, RefreshCcwIcon, ShieldIcon, BuildingIcon, LandmarkIcon, BriefcaseIcon, SparklesIcon, PhoneIcon, FileTextIcon, SettingsIcon, SearchIcon, CheckCircleIcon, DollarSignIcon, CoinsIcon, BanknoteIcon, WalletIcon, PiggyBankIcon, UsersIcon, LockIcon } from 'lucide-react';
const LEAD_SKILLS = [{
  id: 'Purchase',
  icon: HomeIcon
}, {
  id: 'Refinance',
  icon: RefreshCcwIcon
}, {
  id: 'VA',
  icon: ShieldIcon
}, {
  id: 'FHA',
  icon: BuildingIcon
}, {
  id: 'Jumbo',
  icon: LandmarkIcon
}, {
  id: 'Conventional',
  icon: BriefcaseIcon
}];
const PULL_STAGES = [{
  id: 'New',
  icon: SparklesIcon
}, {
  id: 'Contacted',
  icon: PhoneIcon
}, {
  id: 'Application',
  icon: FileTextIcon
}, {
  id: 'Processing',
  icon: SettingsIcon
}, {
  id: 'Underwriting',
  icon: SearchIcon
}, {
  id: 'Clear to Close',
  icon: CheckCircleIcon
}];
const LOAN_RANGES = [{
  id: '< $100k',
  icon: DollarSignIcon
}, {
  id: '$100k - $300k',
  icon: CoinsIcon
}, {
  id: '$300k - $500k',
  icon: BanknoteIcon
}, {
  id: '$500k - $750k',
  icon: WalletIcon
}, {
  id: '> $750k',
  icon: PiggyBankIcon
}];
const TEAMS = ['Alpha Team', 'Beta Team', 'Retail West', 'Retail East', 'Direct to Consumer'];
const CALL_CENTERS = ['Jungo Call Center', 'Default Call Center', 'Sales Call Center', 'Support Call Center'];
const PUBLIC_GROUPS = ['All Loan Officers', 'All Processors', 'All Sales', 'Marketing Team', 'Support Team', 'Management', 'Compliance', 'Training'];
const PERMISSION_SETS = ['Loan Officer Permission Set', 'Processor Permission Set', 'Sales User'];
export function AccessConfig({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const toggleArrayItem = (field: keyof typeof data, value: string) => {
    const current = data[field] as string[];
    const newValues = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    updateData({
      [field]: newValues
    });
  };
  const SectionHeader = ({
    title
  }: {
    title: string;
  }) => <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-4 mt-8 first:mt-0" data-id="element-119">
      {title}
    </h3>;
  const IconTileGrid = ({
    field,
    options,
    cols = 3
  }: {
    field: keyof typeof data;
    options: {
      id: string;
      icon: React.ElementType;
    }[];
    cols?: number;
  }) => <div className={`grid gap-3 grid-cols-2 md:grid-cols-${cols}`} data-id="element-120">
      {options.map(opt => {
      const isSelected = (data[field] as string[]).includes(opt.id);
      const Icon = opt.icon;
      return <button key={opt.id} onClick={() => toggleArrayItem(field, opt.id)} className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center ${isSelected ? 'border-[#2563EB] bg-blue-50 shadow-sm ring-1 ring-[#2563EB]/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-121">
            <Icon className={`w-6 h-6 mb-2 transition-colors ${isSelected ? 'text-[#2563EB]' : 'text-gray-400'}`} data-id="element-122" />
            <span className={`text-xs font-medium transition-colors ${isSelected ? 'text-[#2563EB]' : 'text-gray-600'}`} data-id="element-123">
              {opt.id}
            </span>
          </button>;
    })}
    </div>;
  const TextTileGrid = ({
    field,
    options,
    icon: Icon
  }: {
    field: keyof typeof data;
    options: string[];
    icon: React.ElementType;
  }) => <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-3" data-id="element-124">
      {options.map(opt => {
      const isSelected = (data[field] as string[]).includes(opt);
      return <button key={opt} onClick={() => toggleArrayItem(field, opt)} className={`flex items-center p-2.5 rounded-lg border transition-all text-left ${isSelected ? 'border-[#2563EB] bg-blue-50 shadow-sm ring-1 ring-[#2563EB]/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-125">
            <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 transition-colors ${isSelected ? 'text-[#2563EB]' : 'text-gray-400'}`} data-id="element-126" />
            <span className={`text-xs font-medium truncate transition-colors ${isSelected ? 'text-[#2563EB]' : 'text-gray-600'}`} data-id="element-127">
              {opt}
            </span>
          </button>;
    })}
    </div>;
  const inputClass = 'w-full border-0 border-b border-gray-300 rounded-none px-0 py-2 text-sm focus:outline-none focus:border-[#2563EB] focus:ring-0 bg-transparent transition-colors';
  const selectClass = `${inputClass} appearance-none cursor-pointer`;
  const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2';
  const hasEinsteinEligiblePerm = data.permissionSets.includes('Loan Officer Permission Set') || data.permissionSets.includes('Processor Permission Set');
  return <div className="space-y-2" data-id="element-128">
      <SectionHeader title="Skills & Operational" data-id="element-129" />
      <div className="space-y-8" data-id="element-130">
        <div data-id="element-131">
          <label className={labelClass} data-id="element-132">Lead Skills</label>
          <IconTileGrid field="leadSkills" options={LEAD_SKILLS} cols={3} data-id="element-133" />
        </div>

        <div data-id="element-134">
          <label className={labelClass} data-id="element-135">Eligible Pull Stages</label>
          <IconTileGrid field="eligiblePullStages" options={PULL_STAGES} cols={3} data-id="element-136" />
        </div>

        <div data-id="element-137">
          <label className={labelClass} data-id="element-138">Loan Amount Range</label>
          <IconTileGrid field="loanAmountRange" options={LOAN_RANGES} cols={5} data-id="element-139" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 pt-4 border-t border-gray-100" data-id="element-140">
          <div className="relative" data-id="element-141">
            <label className={labelClass} data-id="element-142">Team</label>
            <select value={data.team} onChange={e => updateData({
            team: e.target.value
          })} className={selectClass} data-id="element-143">
              <option value="" disabled data-id="element-144">
                Select Team
              </option>
              {TEAMS.map(t => <option key={t} value={t} data-id="element-145">
                  {t}
                </option>)}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 top-6 flex items-center px-2 text-gray-400" data-id="element-146">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-147">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-148"></path>
              </svg>
            </div>
          </div>
          <div data-id="element-149">
            <label className={labelClass} data-id="element-150">Ultimate Max Leads</label>
            <input type="number" value={data.ultimateMaxLeads} onChange={e => updateData({
            ultimateMaxLeads: e.target.value ? Number(e.target.value) : ''
          })} className={inputClass} placeholder="e.g. 50" data-id="element-151" />
          </div>
          <div data-id="element-152">
            <label className={labelClass} data-id="element-153">Application Link</label>
            <input type="url" value={data.applicationLink} onChange={e => updateData({
            applicationLink: e.target.value
          })} className={inputClass} placeholder="https://..." data-id="element-154" />
          </div>
        </div>
      </div>

      <SectionHeader title="Call Center & Groups" data-id="element-155" />
      <div className="space-y-6" data-id="element-156">
        <div className="relative max-w-md" data-id="element-157">
          <label className={labelClass} data-id="element-158">Call Center</label>
          <select value={data.callCenter} onChange={e => updateData({
          callCenter: e.target.value
        })} className={selectClass} data-id="element-159">
            <option value="" disabled data-id="element-160">
              Select Call Center
            </option>
            {CALL_CENTERS.map(c => <option key={c} value={c} data-id="element-161">
                {c}
              </option>)}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 top-6 flex items-center px-2 text-gray-400" data-id="element-162">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-163">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-164"></path>
            </svg>
          </div>
        </div>

        <div data-id="element-165">
          <label className={labelClass} data-id="element-166">Public Groups</label>
          <TextTileGrid field="publicGroups" options={PUBLIC_GROUPS} icon={UsersIcon} data-id="element-167" />
        </div>
      </div>

      <SectionHeader title="Permissions" data-id="element-168" />
      <div className="space-y-4" data-id="element-169">
        <TextTileGrid field="permissionSets" options={PERMISSION_SETS} icon={LockIcon} data-id="element-170" />

        <div className={`mt-4 rounded-lg p-3.5 flex items-start space-x-3 text-sm transition-colors ${hasEinsteinEligiblePerm ? 'bg-blue-50 text-blue-800 border border-blue-100' : 'bg-gray-50 text-gray-500 border border-gray-100'}`} data-id="element-171">
          <InfoIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-172" />
          <p data-id="element-173">
            {hasEinsteinEligiblePerm ? 'Einstein Activity Capture will be auto-assigned (eligible permission set selected).' : 'Einstein Activity Capture requires Loan Officer or Processor permission set.'}
          </p>
        </div>
      </div>
    </div>;
}